// 여론 분석 (2026-10-15) — '유튜브 댓글 분석' 메뉴 안에 [유튜브 댓글] [주식 커뮤니티] 탭
// 주식 커뮤니티 지수: 네이버 종목토론 + 토스증권 커뮤니티 + 아카라이브·위폴 글을 (다음 종목토론은 글이 적어 2026-10-15 제외) AI 가 긍정·중립·부정으로 판정해 0~100 으로 (서버 server/community.js)
//   시장 전체는 평일 09:30·12:00·16:00 에 자동 계산, 종목은 검색해서 바로 분석
//   (2026-10-15) 지수를 '부정 75% · 긍정 25%' 처럼 % 로 표시 — 의견 글(긍정+부정) 가운데 비율, 서버 index = 긍정 % 라서 부정 % = 100 - index
//   시장 지수는 지수 ETF 토론방 + 시장 전체 이야기 글만으로 계산(basis='market'), 지수 ETF 토론방별 분위기도 따로 보여 줌
import { useState, useEffect, useRef, useMemo } from "react";
import { mono, BASE_URL } from "./utils";
import { Ico } from "./lib/icons";
import YoutubeComments from "./YoutubeComments";

const card = { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 14, padding: 20, marginBottom: 14 };
const POS = "#10b981", NEG = "#ef4444", NEU = "#94a3b8", SAR = "#f59e0b";
const SRC = { naver: "네이버 종목토론", toss: "토스증권 커뮤니티", arca: "아카라이브", wepoll: "위폴", daum: "다음 종목토론" };
// 지수 구간 (서버 labelOf 와 같은 기준) — 공포(빨강) · 중립(회색) · 낙관(초록) 세 갈래, 끝 구간은 진하게
const ZONES = [
  { to: 20, label: "극단적 공포", color: "#dc2626" },
  { to: 40, label: "공포", color: "#f87171" },
  { to: 60, label: "중립", color: "#94a3b8" },
  { to: 80, label: "낙관", color: "#34d399" },
  { to: 100, label: "과열", color: "#059669" },
];
const negOf = (v) => v == null ? null : 100 - v;
const zoneOf = (v) => v == null ? null : ZONES.find(z => v <= z.to) || ZONES[ZONES.length - 1];
const fmtTime = (iso) => { const t = Date.parse(iso || ""); if (isNaN(t)) return ""; const k = new Date(t + 9 * 3600 * 1000).toISOString(); return `${+k.slice(5, 7)}/${+k.slice(8, 10)} ${k.slice(11, 16)}`; };
const ago = (ms) => { if (!ms) return ""; const m = Math.round((Date.now() - ms) / 60000); return m < 1 ? "방금" : m < 60 ? `${m}분 전` : m < 1440 ? `${Math.round(m / 60)}시간 전` : `${Math.round(m / 1440)}일 전`; };

const Title = ({ icon, children, right }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
    <Ico n={icon} /><span style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{children}</span>
    {right && <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-muted)", ...mono }}>{right}</span>}
  </div>
);

// 부정 % · 긍정 % — 큰 쪽을 크게, 아래 막대는 빨강(부정)·초록(긍정) 비율 (v = 서버 index = 긍정 %)
function Gauge({ v, label, small }) {
  const z = zoneOf(v), neg = negOf(v);
  const negBig = v == null || neg >= v;
  const big = (txt, pct, color) => <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
    <span style={{ fontSize: small ? 14 : 16, fontWeight: 700, color }}>{txt}</span>
    <span style={{ fontSize: small ? 34 : 46, fontWeight: 800, color: "var(--text)", lineHeight: 1, ...mono }}>{pct ?? "—"}<span style={{ fontSize: small ? 18 : 24 }}>{pct != null ? "%" : ""}</span></span>
  </span>;
  const sm = (txt, pct, color) => <span style={{ fontSize: small ? 13 : 14, color: "var(--text-sub)" }}><span style={{ color, fontWeight: 700 }}>{txt}</span> <b style={{ color: "var(--text)", ...mono }}>{pct}%</b></span>;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        {negBig ? big("부정", neg, NEG) : big("긍정", v, POS)}
        {v != null && (negBig ? sm("긍정", v, POS) : sm("부정", neg, NEG))}
        <span style={{ fontSize: small ? 12.5 : 13.5, fontWeight: 700, color: "var(--text)", display: "inline-flex", alignItems: "center", gap: 6 }}>
          {z && <span style={{ width: 10, height: 10, borderRadius: 3, background: z.color, display: "inline-block" }} />}{label || (z ? z.label : "의견 부족")}
        </span>
      </div>
      <SplitBar v={v} h={12} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "var(--text-muted)", marginTop: 5 }}>
        <span>부정</span><span>의견 글 가운데 비율 · 중립 제외 · 추천 많은 글은 조금 더 반영</span><span>긍정</span>
      </div>
    </div>
  );
}
// 왼쪽 빨강 = 부정 %, 오른쪽 초록 = 긍정 %, 가운데 50 표시
function SplitBar({ v, h = 8 }) {
  return (
    <div style={{ position: "relative", height: h, borderRadius: h / 2, overflow: "hidden", background: "var(--bg-input)", display: "flex" }}>
      {v != null && <><div style={{ width: `${negOf(v)}%`, background: NEG }} /><div style={{ flex: 1, background: POS }} /></>}
      <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 2, marginLeft: -1, background: "var(--bg-card)", opacity: 0.9 }} />
    </div>
  );
}
const pctText = (v) => v == null ? "의견 부족" : `부정 ${negOf(v)}% · 긍정 ${v}%`;

function Counts({ t, all }) {
  if (!t) return null;
  const item = (c, l, n) => <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: c }} />{l} <b style={{ color: "var(--text)", ...mono }}>{n}</b></span>;
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12.5, color: "var(--text-sub)" }}>
      {item(POS, "긍정", t.pos)}{item(NEU, "중립", t.neu)}{item(NEG, "부정", t.neg)}
      {t.sarcasm > 0 && item(SAR, "비꼼", t.sarcasm)}
      <span style={{ color: "var(--text-muted)" }}>글 {t.n}개</span>
      {t.holder && <span style={{ width: "100%", color: "var(--text-sub)" }} title="토스증권에서 '보유 중' 표시가 붙은 글만 따로 계산">
        실제 보유자 <b style={{ color: "var(--text)", ...mono }}>{pctText(t.holder.index)}</b>{t.holder.index != null ? ` (${t.holder.label})` : ""} · 보유자 글 {t.holder.n}개
      </span>}
      {all && t.basis === "market" && <span style={{ width: "100%", color: "var(--text-muted)", fontSize: 12 }}>
        시장 전체 이야기 글 {t.n}개 기준 (모은 글 {all.n}개 중) · 개별 종목 글까지 모두 넣으면 {pctText(all.index)}
      </span>}
      {all && t.basis === "all" && <span style={{ width: "100%", color: "var(--text-muted)", fontSize: 12 }}>시장 전체 이야기 글이 적어 모은 글 전체로 계산했습니다.</span>}
    </div>
  );
}

// 출처별 지수 + 읽지 못한 출처 표시
function SourceRows({ bySource, sources }) {
  const keys = Object.keys(SRC).filter(k => (bySource && bySource[k]) || (sources && sources[k]));
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {keys.map(k => {
        const s = bySource?.[k], st = sources?.[k];
        const failed = st && !s && st.errors?.length;
        return (
          <div key={k} style={{ display: "grid", gridTemplateColumns: "118px 1fr auto", gap: 10, alignItems: "center", fontSize: 12.5 }}>
            <span style={{ color: "var(--text)", fontWeight: 600 }}>{SRC[k]}</span>
            {failed
              ? <span style={{ color: "var(--text-muted)", fontSize: 12 }}>읽지 못함 ({st.errors[0]}) — 이번 계산에서 제외</span>
              : <div title={s ? `긍정 ${s.pos} · 중립 ${s.neu} · 부정 ${s.neg}` : ""}><SplitBar v={s?.index} /></div>}
            <span style={{ textAlign: "right", color: "var(--text-sub)", ...mono }}>{failed ? "" : s?.index != null ? `부정 ${negOf(s.index)}% · ${s.n}개` : `— · ${s?.n || 0}개`}</span>
          </div>
        );
      })}
    </div>
  );
}

// 부정 % 추이 (시장 전체, 하루 3번 점) — 위로 갈수록 부정이 많음, 50 기준선, 점선 = 실제 보유자
function Trend({ history }) {
  const pts = (history || []).filter(h => h.index != null).map(h => ({ ...h, neg: negOf(h.index), hneg: h.holder != null ? negOf(h.holder) : null }));
  const [hover, setHover] = useState(null);
  const W = 720, H = 190, P = { l: 30, r: 22, t: 12, b: 26 };
  if (pts.length < 2) return <div style={{ fontSize: 12.5, color: "var(--text-muted)", padding: "18px 0" }}>계산이 두 번 이상 쌓이면 추이가 그려집니다. (평일 09:30 · 12:00 · 16:00)</div>;
  const x = (i) => P.l + (W - P.l - P.r) * (i / (pts.length - 1));
  const y = (v) => P.t + (H - P.t - P.b) * (1 - v / 100);
  const path = pts.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.neg).toFixed(1)}`).join(" ");
  let hpath = "", pen = false;
  pts.forEach((p, i) => { if (p.hneg == null) { pen = false; return; } hpath += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(p.hneg).toFixed(1)} `; pen = true; });
  const step = Math.max(1, Math.ceil(pts.length / 6));
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width * W;
    const i = Math.round((px - P.l) / (W - P.l - P.r) * (pts.length - 1));
    setHover(Math.max(0, Math.min(pts.length - 1, i)));
  };
  const hp = hover != null ? pts[hover] : null;
  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        {[0, 20, 40, 60, 80, 100].map(v => (
          <g key={v}>
            <line x1={P.l} x2={W - P.r} y1={y(v)} y2={y(v)} stroke="var(--border)" strokeWidth="1" strokeDasharray={v === 50 ? "" : "2 4"} opacity={v === 50 ? 1 : 0.6} />
            <text x={P.l - 6} y={y(v) + 3} textAnchor="end" fontSize="10" fill="var(--text-muted)" style={mono}>{v}%</text>
          </g>
        ))}
        <line x1={P.l} x2={W - P.r} y1={y(50)} y2={y(50)} stroke="var(--text-muted)" strokeWidth="1" />
        {hpath && <path d={hpath} fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeDasharray="4 4" />}
        <path d={path} fill="none" stroke={NEG} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => i % step === 0 || i === pts.length - 1 ? <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="var(--text-muted)" style={mono}>{fmtTime(p.at).split(" ")[0]}</text> : null)}
        {pts.map((p, i) => <circle key={`c${i}`} cx={x(i)} cy={y(p.neg)} r={hover === i ? 5 : 3} fill={zoneOf(p.index).color} stroke="var(--bg-card)" strokeWidth="2" />)}
        {hp && <line x1={x(hover)} x2={x(hover)} y1={P.t} y2={H - P.b} stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="3 3" />}
      </svg>
      {hp && (
        <div style={{ position: "absolute", top: 0, left: `${Math.min(80, Math.max(0, hover / (pts.length - 1) * 100 - 8))}%`, background: "var(--bg-modal, var(--bg-card))", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 10px", fontSize: 12, color: "var(--text)", pointerEvents: "none", boxShadow: "0 6px 20px rgba(0,0,0,0.25)" }}>
          <div style={{ color: "var(--text-muted)", ...mono }}>{fmtTime(hp.at)}</div>
          <div>부정 <b style={mono}>{hp.neg}%</b> · {zoneOf(hp.index).label} · 글 {hp.n}개{hp.basis === "market" ? "" : " (종목 글 포함 기준)"}</div>
          {hp.hneg != null && <div style={{ color: "var(--text-sub)", fontSize: 11 }}>실제 보유자 부정 {hp.hneg}%</div>}
          {hp.bySource && <div style={{ color: "var(--text-sub)", fontSize: 11 }}>{Object.entries(hp.bySource).filter(([, v]) => v != null).map(([k, v]) => `${SRC[k]} ${negOf(v)}%`).join(" · ")}</div>}
        </div>
      )}
    </div>
  );
}

function Samples({ samples }) {
  if (!samples) return null;
  const col = (list, color, label) => (
    <div style={{ flex: 1, minWidth: 260 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />{label}</div>
      {list.length ? list.map((p, i) => (
        <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" style={{ display: "block", padding: "7px 0", borderTop: i ? "1px solid var(--border)" : "none", textDecoration: "none", color: "var(--text)", fontSize: 13, lineHeight: 1.5 }}>
          {p.title}
          <span style={{ display: "block", fontSize: 11, color: "var(--text-muted)" }}>{SRC[p.src]}{p.holder ? " · 보유자" : ""}{p.likes ? ` · 추천 ${p.likes}` : ""}{p.sarcasm ? " · 비꼼" : ""}{p.stock && p.stock !== "시장" ? ` · ${p.stock}` : ""}{p.at ? ` · ${ago(p.at)}` : ""}</span>
        </a>
      )) : <div style={{ fontSize: 12, color: "var(--text-muted)" }}>없음</div>}
    </div>
  );
  return <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>{col(samples.pos, POS, "긍정적인 글")}{col(samples.neg, NEG, "부정적인 글")}</div>;
}

function Summary({ r }) {
  if (!r?.summary?.length && !r?.keywords?.length) return null;
  return (
    <div style={{ marginTop: 14 }}>
      {r.summary.map((s, i) => <div key={i} style={{ fontSize: 13.5, color: "var(--text)", lineHeight: 1.75, marginBottom: 4 }}>{s}</div>)}
      {r.keywords?.length > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>{r.keywords.map((k, i) => <span key={i} style={{ fontSize: 11.5, padding: "3px 9px", borderRadius: 999, background: "var(--bg-input)", color: "var(--text-sub)" }}>#{k}</span>)}</div>}
    </div>
  );
}

// ── 종목 검색 ──
function StockSearch({ pick }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [st, setSt] = useState(null);
  const [filter, setFilter] = useState("all");
  const timer = useRef(null), alive = useRef(true), seq = useRef(0);
  useEffect(() => () => { alive.current = false; clearTimeout(timer.current); }, []);
  useEffect(() => {
    if (!q.trim() || /^\d{6}$/.test(q.trim())) { setList([]); return; }
    const my = ++seq.current;
    const t = setTimeout(() => fetch(`${BASE_URL}/stockSearch?q=${encodeURIComponent(q.trim())}`).then(r => r.ok ? r.json() : []).then(d => { if (my === seq.current) setList(Array.isArray(d) ? d.slice(0, 8) : []); }).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const run = (code, name) => {
    clearTimeout(timer.current); setOpen(false); setQ(name || code); setFilter("all");
    setSt({ status: "running", step: "요청 보내는 중", name, code });
    const poll = () => fetch(`${BASE_URL}/community/stock?code=${code}&name=${encodeURIComponent(name || "")}`)
      .then(r => r.json().then(j => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!alive.current) return;
        if (!ok || j.error) { setSt({ status: "error", error: j.error || "분석에 실패했습니다.", name, code }); return; }
        setSt({ ...j, name: j.name || name, code });
        if (j.status === "running") timer.current = setTimeout(poll, 3000);
      })
      .catch(() => { if (alive.current) setSt({ status: "error", error: "서버에 연결하지 못했습니다.", name, code }); });
    poll();
  };
  // '많이 이야기되는 종목'을 누르면: 코드가 없으면 이름으로 찾아서 분석
  useEffect(() => {
    if (!pick) return;
    if (pick.code) { run(pick.code, pick.name); return; }
    fetch(`${BASE_URL}/stockSearch?q=${encodeURIComponent(pick.name)}`).then(r => r.ok ? r.json() : []).then(l => {
      const m = Array.isArray(l) && (l.find(x => x.name === pick.name) || l[0]);
      if (m) run(m.code, m.name); else { setQ(pick.name); setSt({ status: "error", error: `'${pick.name}' 종목코드를 찾지 못했습니다.` }); }
    }).catch(() => {});
  }, [pick]); // eslint-disable-line
  const submit = (e) => { e.preventDefault(); const v = q.trim(); if (/^\d{6}$/.test(v)) run(v, ""); else if (list[0]) run(list[0].code, list[0].name); };
  const posts = useMemo(() => (st?.posts || []).filter(p => filter === "all" || p.s === filter), [st, filter]);
  return (
    <div>
      <form onSubmit={submit} style={{ position: "relative", display: "flex", gap: 8 }}>
        <input value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder="종목명 또는 종목코드 (예: 삼성전자, 005930)"
          style={{ flex: 1, background: "var(--bg-input)", border: "1px solid var(--border)", borderRadius: 10, color: "var(--text)", padding: "11px 14px", fontSize: 14, outline: "none", fontFamily: "inherit" }} />
        <button type="submit" disabled={st?.status === "running"} style={{ padding: "0 18px", borderRadius: 10, border: "none", background: "var(--primary)", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", opacity: st?.status === "running" ? 0.6 : 1 }}>분석</button>
        {open && list.length > 0 && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 80, zIndex: 20, marginTop: 4, background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 10px 30px rgba(0,0,0,0.3)", overflow: "hidden" }}>
            {list.map(s => <div key={s.code} onMouseDown={() => run(s.code, s.name)} style={{ padding: "9px 14px", fontSize: 13, cursor: "pointer", color: "var(--text)", display: "flex", justifyContent: "space-between" }} onMouseEnter={e => { e.currentTarget.style.background = "var(--bg-input)"; }} onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>{s.name}<span style={{ color: "var(--text-muted)", ...mono }}>{s.code}</span></div>)}
          </div>
        )}
      </form>
      {st?.status === "running" && (
        <div style={{ marginTop: 14, fontSize: 13, color: "var(--text-sub)" }}>
          <b style={{ color: "var(--text)" }}>{st.name || st.code}</b> — {st.step}{st.total ? ` (${st.done || 0}/${st.total})` : ""}{st.elapsed ? ` · ${st.elapsed}초` : ""}
          <div style={{ height: 4, background: "var(--bg-input)", borderRadius: 2, marginTop: 8, overflow: "hidden" }}><div style={{ height: "100%", width: `${st.total ? Math.max(8, (st.done || 0) / st.total * 100) : 15}%`, background: "var(--primary)", transition: "width .4s" }} /></div>
        </div>
      )}
      {st?.status === "error" && <div style={{ marginTop: 14, fontSize: 13, color: NEG }}>{st.error}</div>}
      {st?.status === "done" && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 12 }}>{st.name || st.code} <span style={{ color: "var(--text-muted)", fontWeight: 500, fontSize: 12, ...mono }}>{st.code} · 최근 3일 · {fmtTime(st.at)}</span></div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: 20 }}>
            <div><Gauge v={st.total.index} label={st.total.label} small /><div style={{ marginTop: 12 }}><Counts t={st.total} /></div></div>
            <SourceRows bySource={st.bySource} sources={st.sources} />
          </div>
          <Summary r={st} />
          <div style={{ display: "flex", gap: 6, margin: "16px 0 8px", flexWrap: "wrap" }}>
            {[["all", "전체"], ["pos", "긍정"], ["neg", "부정"], ["neu", "중립"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} style={{ padding: "5px 12px", borderRadius: 999, fontSize: 12, cursor: "pointer", fontFamily: "inherit", border: "1px solid " + (filter === k ? "var(--primary)" : "var(--border)"), background: filter === k ? "var(--sb-active-bg)" : "transparent", color: filter === k ? "var(--sb-active-text)" : "var(--text-sub)" }}>{l}</button>
            ))}
          </div>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {posts.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" style={{ display: "flex", gap: 10, padding: "8px 2px", borderTop: "1px solid var(--border)", textDecoration: "none", alignItems: "baseline" }}>
                <span style={{ flex: "0 0 auto", width: 8, height: 8, borderRadius: 2, background: p.s === "pos" ? POS : p.s === "neg" ? NEG : NEU, marginTop: 5 }} title={p.s === "pos" ? "긍정" : p.s === "neg" ? "부정" : "중립"} />
                <span style={{ flex: 1, color: "var(--text)", fontSize: 13, lineHeight: 1.5 }}>{p.title}{p.sarcasm && <span style={{ color: SAR, fontSize: 11 }}> · 비꼼</span>}</span>
                <span style={{ flex: "0 0 auto", fontSize: 11, color: "var(--text-muted)" }}>{SRC[p.src]}{p.holder ? " · 보유자" : ""}{p.likes ? ` · ${p.likes}` : ""}{p.at ? ` · ${ago(p.at)}` : ""}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CommunityIndex() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [pick, setPick] = useState(null);
  const searchRef = useRef(null);
  useEffect(() => {
    let alive = true, t = null;
    const load = () => fetch(`${BASE_URL}/community`).then(r => r.json()).then(j => {
      if (!alive) return;
      setD(j); setErr(null);
      if (j.running && !j.latest) t = setTimeout(load, 5000); // 첫 계산 중이면 5초마다 다시
    }).catch(() => { if (alive) setErr("서버에 연결하지 못했습니다."); });
    load();
    return () => { alive = false; clearTimeout(t); };
  }, []);
  const L = d?.latest;
  return (
    <div>
      <div style={card}>
        <Title icon="messages-square" right={L ? `${fmtTime(L.at)} 기준 · ${L.slot}` : ""}>오늘의 주식 커뮤니티 지수</Title>
        {err && <div style={{ color: NEG, fontSize: 13 }}>{err}</div>}
        {!L && !err && <div style={{ fontSize: 13, color: "var(--text-sub)" }}>{d?.running ? "첫 계산 중입니다. 커뮤니티 글을 모아 판정하는 데 1~2분 걸립니다…" : d?.error ? `계산하지 못했습니다: ${d.error}` : "불러오는 중…"}</div>}
        {L && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 24 }}>
              <div><Gauge v={L.total.index} label={L.total.label} /><div style={{ marginTop: 14 }}><Counts t={L.total} all={L.all} /></div></div>
              <div><div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>출처별 분위기 (모은 글 전체 · 빨강 = 부정 비율)</div><SourceRows bySource={L.bySource} sources={L.sources} /></div>
            </div>
            <Summary r={L} />
          </>
        )}
      </div>

      <div style={card}>
        <Title icon="chart-line" right={d?.schedule}>부정 비율 추이</Title>
        <Trend history={d?.history} />
        <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 6 }}>빨간 선 = 시장 분위기 부정 % (위로 갈수록 공포) · 회색 점선 = 실제 보유자 부정 %</div>
      </div>

      {L?.byEtf?.length > 0 && (
        <div style={card}>
          <Title icon="chart-candlestick">지수 ETF 토론방 분위기</Title>
          <div style={{ display: "grid", gap: 10 }}>
            {L.byEtf.map(e => (
              <div key={e.name} style={{ display: "grid", gridTemplateColumns: "minmax(150px, 240px) 1fr auto", gap: 10, alignItems: "center", fontSize: 12.5 }} title={`긍정 ${e.pos} · 중립 ${e.neu} · 부정 ${e.neg}`}>
                <span style={{ color: "var(--text)", fontWeight: 600 }}>{e.name}{e.inverse && <span style={{ marginLeft: 6, whiteSpace: "nowrap", fontSize: 10.5, padding: "1px 6px", borderRadius: 999, background: "var(--bg-input)", color: "var(--text-sub)", fontWeight: 500 }}>인버스</span>}</span>
                <SplitBar v={e.index} />
                <span style={{ textAlign: "right", color: "var(--text-sub)", ...mono }}>{e.index != null ? `부정 ${negOf(e.index)}%` : "의견 부족"} · {e.n}개</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 10, lineHeight: 1.7 }}>모두 '시장 방향' 기준입니다. 인버스는 시장이 내릴 때 돈을 버는 상품이라, 인버스 토론방의 수익 환호는 시장 부정으로 셉니다.</div>
        </div>
      )}

      {L?.byStock?.length > 0 && (
        <div style={card}>
          <Title icon="flame">많이 이야기되는 종목</Title>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {L.byStock.map(s => {
              const z = zoneOf(s.index);
              return (
                <button key={s.name} onClick={() => { const m = (d.marketStocks || []).find(x => x.name === s.name); setPick({ code: m?.code, name: s.name, t: Date.now() }); searchRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  title={`긍정 ${s.pos} · 중립 ${s.neu} · 부정 ${s.neg}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 12px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text)", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: z ? z.color : NEU }} />{s.name}
                  <span style={{ color: "var(--text-muted)", fontSize: 11.5, ...mono }}>{s.index != null ? `부정 ${negOf(s.index)}%` : "—"} · {s.n}개</span>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 8 }}>누르면 아래에서 그 종목 반응을 따로 분석합니다.</div>
        </div>
      )}

      {L?.samples && (
        <div style={card}>
          <Title icon="newspaper">대표 글</Title>
          <Samples samples={L.samples} />
        </div>
      )}

      <div style={card} ref={searchRef}>
        <Title icon="search">종목별 커뮤니티 반응</Title>
        <StockSearch pick={pick} />
      </div>

      <div style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.8, padding: "0 4px 20px" }}>
        · 출처: 네이버페이 증권 종목토론실, 토스증권 커뮤니티, 아카라이브 주식 채널, 위폴 경제 게시판의 공개 글. '실제 보유자 지수'는 토스에서 보유 중 표시가 붙은 글만 계산합니다. 다음 종목토론은 글이 적어서, 블라인드는 로그인이 필요해서, 디시·에펨코리아 주식 게시판은 사이트가 자동 수집을 막아 두어서 제외했습니다.<br />
        · 시장 분위기는 코스피·코스닥 지수 ETF 토론방({(d?.marketEtfs || []).map(s => s.name).join("·") || "KODEX 200 등 5개"}) 글과, 시총 상위 {(d?.marketStocks || []).map(s => s.name).join("·") || "6종목"} 토론방·두 게시판 글 가운데 AI 가 시장 전체 이야기로 본 글(최근 24시간)로 계산합니다. 한 종목 이야기만 있는 글은 '많이 이야기되는 종목'에만 들어갑니다. AI 판정이라 반어·은어를 틀릴 수 있고, 투자 판단의 근거가 아니라 분위기를 보는 참고 지표입니다.
      </div>
    </div>
  );
}

// 메뉴 화면: [유튜브 댓글] [주식 커뮤니티]
const TAB_KEY = "dgds-opinion-tab";
export default function OpinionPage() {
  const [tab, setTab] = useState(() => { try { return localStorage.getItem(TAB_KEY) || "yt"; } catch { return "yt"; } });
  const choose = (t) => { setTab(t); try { localStorage.setItem(TAB_KEY, t); } catch {} };
  return (
    <div style={{ maxWidth: 1080, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {[["yt", "유튜브 댓글", "square-play"], ["comm", "주식 커뮤니티", "messages-square"]].map(([k, l, ic]) => (
          <button key={k} onClick={() => choose(k)} style={{ padding: "8px 14px", borderRadius: 9, fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: tab === k ? 700 : 500, border: "1px solid " + (tab === k ? "var(--primary)" : "var(--border)"), background: tab === k ? "var(--sb-active-bg)" : "var(--bg-card)", color: tab === k ? "var(--sb-active-text)" : "var(--text-sub)" }}>
            <Ico n={ic} tint={false} />{l}
          </button>
        ))}
      </div>
      {tab === "yt" ? <YoutubeComments /> : <CommunityIndex />}
    </div>
  );
}
